/* KBW — the assembly line follows its cabinet.

   THE PROBLEM THIS SOLVES, and it is money rather than tidiness. A surcharge
   line with its own quantity stepper can drift away from the cabinet it belongs
   to: two cabinets and three assemblies, or one assembly against three cabinets.
   The customer is then charged for whatever they left behind. The surcharge is
   not a thing you buy a quantity of — it is a property of the cabinet above it.

   So the stepper is gone from that row (kbw-cart-assembly.css) and this file
   keeps the two in step:

     • change a cabinet's quantity  -> its assembly line follows, same update
     • remove a cabinet             -> its assembly line goes with it
     • remove the assembly line     -> only that line goes; deciding to build it
                                       yourself is a legitimate thing to do, and
                                       nothing here interferes with it

   WHY IT RECONCILES RATHER THAN INTERCEPTS. Quantity can change from the cart
   page stepper, the drawer stepper, a remove button, the quantity input, a
   browser back, or another script entirely. Hooking each of those means finding
   all of them and finding them again whenever the theme changes. Instead this
   reads the cart AFTER any change and repairs what it finds: the assembly line
   is set to its cabinet's quantity, and an assembly line whose cabinet has gone
   is set to zero, which removes it. One rule, applied to the finished state,
   and it cannot be bypassed by a control nobody thought of.

   The pairing is already in the line: `_kbw_assembly` marks the surcharge and
   `_kbw_for` holds the cabinet's variant id. Both are underscore-prefixed, so
   Shopify keeps them out of the cart display, the checkout and the order.

   The cart page and the drawer go through this same file, and re-render through
   the theme's own `getSectionsToRender`, which already knows about both. */

(function () {
  'use strict';

  var busy = false;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* The theme's own section list: the cart icon's badge, the shipping block, and
     whichever of the cart page or the drawer is on screen. Asking it rather than
     naming selectors ourselves is what keeps the two surfaces from drifting. */
  function sections() {
    var helper = window.xCartHelper;
    if (!helper || typeof helper.getSectionsToRender !== 'function') return [];
    var id = null;
    if (window.Maximize && window.Maximize.isCartPage) {
      var items = document.querySelector('#MainCart__Items');
      var sec = items && items.closest('[id^="shopify-section-"]');
      if (sec) id = sec.id.replace('shopify-section-', '');
    } else {
      var drawer = document.querySelector('#CartDrawer');
      if (drawer) id = drawer.dataset.sectionId;
    }
    if (!id) return [];
    return helper.getSectionsToRender(id) || [];
  }

  /* What would have to change for every assembly line to match its cabinet.
     Returns null when the cart is already right, which is the common case and
     the one that must cost nothing. */
  function plan(cart) {
    var wanted = {};
    cart.items.forEach(function (i) {
      var p = i.properties || {};
      if (p._kbw_assembly) return;
      wanted[i.variant_id] = (wanted[i.variant_id] || 0) + i.quantity;
    });

    var updates = {};
    var changed = false;
    cart.items.forEach(function (i) {
      var p = i.properties || {};
      if (!p._kbw_assembly) return;
      /* No `_kbw_for` means the line predates the pairing or was hand-made.
         Left alone: removing somebody's paid-for line on a guess is worse than
         leaving it visible for them to decide about. */
      if (!p._kbw_for) return;
      var want = wanted[p._kbw_for] || 0;
      if (want !== i.quantity) {
        updates[i.key] = want;
        changed = true;
      }
    });
    return changed ? updates : null;
  }

  /* Repainting a section is a straight innerHTML replacement, the same way the
     theme's own cart code does it — and so it inherits the same hazard. The
     server renders the drawer not knowing it is open, so the markup that comes
     back carries `is-visible` on neither `#CartDrawer__Wrapper` nor
     `#CartDrawer__Upsell`. Without that class the wrapper is `display: none`
     (cart-drawer.liquid:32-39), while `<cart-drawer>` keeps its `open` attribute
     and the body keeps `overflow: hidden`: an invisible drawer over a page that
     will not scroll, and no way to close it.

     `cart-drawer.js:456-462` and `:472-475` guard against exactly this around
     their own swap. We re-implemented the swap, so we have to carry the guard
     too. Read the state before, put it back after, scoped to `#CartDrawer` —
     the cart page's `#MainCart__Items` holds neither element. */
  function swap(list, res) {
    if (!res || !res.sections) return;
    list.forEach(function (s) {
      var el = document.querySelector(s.selector);
      var html = res.sections[s.id];
      if (!el || !html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var from = doc.querySelector(s.selector) || doc.body;

      if (s.selector === '#CartDrawer') {
        var wrapper = el.querySelector('#CartDrawer__Wrapper');
        var wrapperVisible = !!(wrapper && wrapper.classList.contains('is-visible'));
        el.innerHTML = from.innerHTML;
        if (wrapperVisible) {
          var repainted = el.querySelector('#CartDrawer__Wrapper');
          if (repainted) repainted.classList.add('is-visible');
        }
      } else {
        el.innerHTML = from.innerHTML;
      }
    });

    /* The upsell panel is restored the way the vendor restores it, which is not
       by carrying the old class over: it recomputes from how many upsell items
       the new markup actually holds, and the setter re-adds `is-visible` along
       with the drawer's `upsell-open` attribute (cart-drawer-upsell.js:30-44).
       Same call, same condition — on the cart page there is no drawer upsell. */
    if (!(window.Maximize && window.Maximize.isCartPage)) {
      var drawer = document.querySelector('#CartDrawer');
      var upsellEl = drawer && drawer.querySelector('cart-drawer-upsell');
      if (upsellEl) upsellEl.isOpen = !!document.querySelectorAll('.cart-drawer-upsell__item').length;
    }
  }

  function reconcile() {
    /* The guard is what stops this chasing its own tail: the repair changes the
       cart, which is exactly the thing that triggers a reconcile. */
    if (busy) return Promise.resolve();
    busy = true;

    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) return null;
        var updates = plan(cart);
        if (!updates) return null;

        var list = sections();
        return fetch(root() + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates, sections: list.map(function (s) { return s.id; }) }),
        })
          .then(function (r) { return r.json(); })
          .then(function (res) {
            swap(list, res);

            /* The theme's cart helper keeps its own copy of the item count and
               reads it from the badge only when asked. Left alone after this
               repair it is off by the assembly line, and two things trust it:
               product-form.js:122 (how many items an add just put in) and
               cart-drawer.js:477-483 (whether the next change is announced at
               all). Its own refresh re-reads the badge just repainted above;
               it only stores the number and fires nothing. */
            var helper = window.xCartHelper;
            if (helper && typeof helper.updateCurrentCountItem === 'function') helper.updateCurrentCountItem();

            /* Deliberately no `cart:update` / `maximize:cart:updated` of our
               own. The sections above are already repainted, and firing either
               would ask every listener — including this one — to go round again.

               But a repaint nobody hears about leaves behind anything that
               MIRRORS the header badge instead of being repainted with it: the
               quick-order floating cart copies the badge's count only when the
               theme announces a change (kbw-quick-order.js). The theme announces
               the cabinet's removal, the floating cart copies "1", and then this
               repair empties the cart in silence — the header shows nothing,
               the floating cart keeps saying 1.

               So the repaint is announced under a name of its own, which this
               file does not listen to and the theme does not know: it cannot
               start another reconcile, and it cannot set off the theme's own
               cart handlers. Listeners only read the DOM it has just painted. */
            document.dispatchEvent(new CustomEvent('kbw:cart:reconciled', {
              detail: { itemCount: res && typeof res.item_count === 'number' ? res.item_count : null },
            }));
            return res;
          });
      })
      .catch(function () { /* a cart that cannot be read is left exactly as it is */ })
      .then(function (r) { busy = false; return r; });
  }

  var CART_UPDATED = (window.CART_EVENT && window.CART_EVENT.cartUpdate) || 'maximize:cart:updated';
  document.addEventListener(CART_UPDATED, function () { reconcile(); });
  document.addEventListener('cart:update', function () { reconcile(); });

  /* On load too: a cart can arrive already out of step from an earlier session,
     or from a tab that was open while another changed it. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reconcile(); });
  } else {
    reconcile();
  }

  window.KbwCartAssembly = { reconcile: reconcile };
})();
